import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import * as pdfjsLib from "pdfjs-dist"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowUpRightFromSquare,
  faCheckCircle,
  faLink,
  faNoteSticky,
  faPenToSquare,
  faSpinner,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { CheckCircleIcon } from "@heroicons/react/24/outline"

import { useNana } from "../../hooks/useNana"
import ShowEditor from "./mosaic_page/idea_pane/show_editor"
import { Idea, WebSource, ID, FleetingIdea } from "../../../model/types"
import { SetWebSourceFieldCommand } from "../../../model/commands"
import SourceIdeaCapture from "./sources_page/source_view/source_notes/source_idea_capture"
import NewSourceForm from "./sources_page/new_source_form"
import Modal from "../../components/modal"
import { SourcesPageState } from "./sources_page"
import { FleetingIdeaBox } from "../../components/idea_box"
import * as Commands from "../../../model/commands"
import Button from "../../components/button"
import Hub from "../../../hub"
import { MosaicPageState } from "./mosaic"
import { generatePublicId } from "../../../model/public_id"
import {
  canUseLiveSource,
  hasStoredContentEndpoint,
  hasStoredMarkdown,
  isPdfSource,
  isXStatusSource,
  preferredReaderMode,
  sourceContentUrl,
  SourceReaderMode,
} from "./sources_page/source_content"
import SourceMarkdownView from "./sources_page/source_view/source_notes/source_markdown_view"

const READER_IFRAME_SCALE = 0.8

const pdfWorkerMeta = document.querySelector<HTMLMetaElement>("meta[name='pdf-worker-src']")
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerMeta?.content || "/assets/pdf.worker.js"

export default function SourceReadingView() {
  const { sourceUuid } = useParams() as { sourceUuid: ID }
  const { model, commander, hub } = useNana()
  const navigate = useNavigate()

  const [source, setSource] = useState<WebSource | null>(null)

  const parentRef = React.useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!sourceUuid) navigate("/app")
    const source = model.getSource(sourceUuid)
    if (!source) navigate("/app")
    setSource(source)
  }, [sourceUuid])

  useEffect(() => {
    const keys = [
      hub.on("model:source-updated", ({ id }) => {
        if (id === sourceUuid) setSource(model.getSource(id))
      }),
      hub.on("model:source-deleted", ({ id }) => {
        if (id === sourceUuid) navigate("/app")
      }),
    ]

    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [sourceUuid])

  if (!source) return null

  const close = () => {
    SourcesPageState.openSourceId = source.id
    navigate(`/app/sources`)
  }

  const setReadAndClose = () => {
    if (!source) return
    commander.exec(
      new Commands.SetWebSourceFieldCommand({
        id: source.id,
        field: "status",
        value: "read",
      }),
    )
    SourcesPageState.openSourceId = null
    navigate(`/app/sources`)
  }

  return (
    <>
      <div className="hidden h-full w-full grow overflow-hidden md:flex" ref={parentRef}>
        <div className="flex h-full min-w-[24rem] basis-2/5 shrink-0 flex-col overflow-hidden border-r border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex min-h-0 basis-3/5 flex-col overflow-hidden">
            <div className="webkit-app-drag desktop-traffic-light-inset shrink-0 p-2 flex items-center justify-between text-sm font-bold border-b border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-950">
              <FontAwesomeIcon
                icon={faXmark}
                onClick={() => close()}
                className="webkit-app-no-drag p-2 w-5 h-5 cursor-pointer hover:text-sky-500"
              />
              <div className="flex items-stretch justify-between gap-3">
                <NewSourceForm>
                  {(open) => (
                    <FontAwesomeIcon
                      icon={faLink}
                      onClick={() => open()}
                      className="webkit-app-no-drag p-2 w-4 h-4 rounded bg-gray-50 border border-gray-200 hover:bg-sky-100 cursor-pointer"
                    />
                  )}
                </NewSourceForm>

                <button
                  onClick={() => setReadAndClose()}
                  type="button"
                  className="inline-flex items-center gap-x-1.5 rounded-md px-2.5 text-xs text-gray-700 dark:text-gray-200 dark:hover:bg-gray-700  bg-gray-50 border border-gray-200 hover:bg-sky-100 cursor-pointer"
                >
                  <CheckCircleIcon className="h-5 w-5 text-sky-500" />
                  Mark read
                </button>
              </div>
            </div>
            <div className="shrink-0 uppercase p-2 px-2 text-sm font-bold border-b border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300">
              Literature notes
            </div>
            <ShowEditor
              objectKey={source.id}
              initialContent={source?.literatureNotes || ""}
              placeholderText="Notes on and from this source..."
              onPageLinkClick={(idea: Idea) => {
                MosaicPageState.forceOpenLocation = idea.location
                navigate("/app")
              }}
              onUpdate={(newContent, _newSelectionPos) => {
                commander.exec(
                  new SetWebSourceFieldCommand({
                    id: source.id,
                    field: "literatureNotes",
                    value: newContent,
                  }),
                )
              }}
              recreateWhen={(hub: Hub, recreate): string[] => {
                const k = hub.on("model:source-updated", ({ id, fields, originator }) => {
                  if (
                    id === source.id &&
                    fields.hasOwnProperty("literatureNotes") &&
                    originator !== "editor"
                  ) {
                    recreate()
                  }
                })
                return [k]
              }}
            />
          </div>
          <div className="flex min-h-0 basis-2/5 flex-col overflow-hidden">
            <div className="shrink-0 uppercase p-2 px-2 text-sm font-bold border-y border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300">
              Mosaic notes
            </div>
            <SourceIdeaCapture source={source} allowCaptureOfReadSource={false} />
          </div>
        </div>
        <div className="flex h-full min-w-0 flex-1 bg-white">
          <SourceReader source={source} className="grow" />
        </div>
      </div>
      <MobileView source={source} close={close} setReadAndClose={setReadAndClose} />
    </>
  )
}

const MobileView = ({
  source,
  close,
  setReadAndClose,
}: {
  source: WebSource
  close: () => void
  setReadAndClose: () => void
}) => {
  const { model, commander } = useNana()
  const navigate = useNavigate()
  const [showLiteratureNotes, setShowLiteratureNotes] = useState(false)

  return (
    <div className="h-full flex flex-col md:hidden w-full grow">
      <div className="h-full flex flex-col">
        <div className="h-2/3 grow flex justify-stretch items-stretch min-h-0">
          <SourceReader source={source} className="grow bg-white" />
        </div>
        {showLiteratureNotes && (
          <div className="h-1/3 flex-initial flex flex-col border-t-2 border-gray-400 bg-gray-100 dark:bg-gray-900">
            <ShowEditor
              tightPadding={true}
              objectKey={source.id}
              initialContent={source?.literatureNotes || ""}
              placeholderText="Notes on and from this source..."
              onPageLinkClick={(idea: Idea) => {
                MosaicPageState.forceOpenLocation = idea.location
                navigate("/app")
              }}
              onUpdate={(newContent, _newSelectionPos) => {
                commander.exec(
                  new SetWebSourceFieldCommand({
                    id: source.id,
                    field: "literatureNotes",
                    value: newContent,
                  }),
                )
              }}
            />
            <MobileToolbar
              source={source}
              close={close}
              showLiteratureNotes={showLiteratureNotes}
              setShowLiteratureNotes={setShowLiteratureNotes}
              setReadAndClose={setReadAndClose}
            />
          </div>
        )}
        {!showLiteratureNotes && (
          <MobileToolbar
            source={source}
            close={close}
            showLiteratureNotes={showLiteratureNotes}
            setShowLiteratureNotes={setShowLiteratureNotes}
            setReadAndClose={setReadAndClose}
          />
        )}
      </div>
    </div>
  )
}

const SourceReader = ({ source, className }: { source: WebSource; className: string }) => {
  const hasStoredContent = hasStoredContentEndpoint(source)
  const canShowMarkdown = hasStoredMarkdown(source) && !isPdfSource(source)
  const canShowLive = canUseLiveSource(source)
  const isPdf = isPdfSource(source)
  const preferredMode = preferredReaderMode(source)
  const [mode, setMode] = useState<SourceReaderMode>(preferredMode)
  const [iframeLoading, setIframeLoading] = useState(preferredMode === "stored")

  useEffect(() => {
    setMode(preferredMode)
  }, [source.id, preferredMode])

  const effectiveMode: SourceReaderMode = (() => {
    if (mode === "markdown" && canShowMarkdown) return "markdown"
    if (mode === "stored" && hasStoredContent) return "stored"
    if (mode === "live" && canShowLive) return "live"
    if (canShowMarkdown) return "markdown"
    if (hasStoredContent) return "stored"
    return "live"
  })()
  const sourceUrl = source.url || undefined
  const sourceLabel = source.name || sourceUrl || "source"
  const iframeSrc =
    effectiveMode === "stored" && hasStoredContent ? sourceContentUrl(source) : sourceUrl
  const iframeTitle = effectiveMode === "stored" ? `Saved copy of ${sourceLabel}` : sourceLabel
  const iframeScale = isXStatusSource(source) ? 1 : READER_IFRAME_SCALE

  useEffect(() => {
    setIframeLoading(effectiveMode === "stored")
  }, [effectiveMode, iframeSrc])

  return (
    <div className={`${className} flex min-h-0 min-w-0 flex-col bg-white`}>
      <SourceReaderToolbar
        source={source}
        mode={effectiveMode}
        hasStoredContent={hasStoredContent}
        canShowMarkdown={canShowMarkdown}
        canShowLive={canShowLive}
        isPdf={isPdf}
        setMode={setMode}
      />
      {effectiveMode === "markdown" ? (
        <SourceMarkdownView source={source} />
      ) : isPdf && hasStoredContent ? (
        <PdfReader source={source} />
      ) : isPdf ? (
        <div className="flex grow items-center justify-center bg-gray-50 p-6 text-center text-sm text-gray-600">
          <div>
            <div className="font-medium text-gray-800">This PDF is still being attached.</div>
            <div className="mt-1 text-gray-500">Try again in a moment.</div>
          </div>
        </div>
      ) : effectiveMode === "live" && !canShowLive ? (
        <div className="flex grow items-center justify-center bg-gray-50 p-6 text-center text-sm text-gray-600">
          <div>
            <div className="font-medium text-gray-800">
              {sourceUrl ? "This source blocks embedded reading." : "This source has no URL."}
            </div>
            {sourceUrl ? (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-sky-600 hover:text-sky-700"
              >
                Open original
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="relative min-h-0 grow overflow-hidden">
          {iframeLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white text-gray-500 dark:bg-gray-950 dark:text-gray-300">
              <FontAwesomeIcon icon={faSpinner} className="h-5 w-5" spin />
            </div>
          )}
          <iframe
            className="border-0"
            style={{
              height: `${100 / iframeScale}%`,
              transform: `scale(${iframeScale})`,
              transformOrigin: "top left",
              width: `${100 / iframeScale}%`,
            }}
            src={iframeSrc}
            onLoad={() => setIframeLoading(false)}
            sandbox={
              effectiveMode === "stored" ? "allow-popups allow-popups-to-escape-sandbox" : undefined
            }
            title={iframeTitle}
          />
        </div>
      )}
    </div>
  )
}

const SourceReaderToolbar = ({
  source,
  mode,
  hasStoredContent,
  canShowMarkdown,
  canShowLive,
  isPdf,
  setMode,
}: {
  source: WebSource
  mode: SourceReaderMode
  hasStoredContent: boolean
  canShowMarkdown: boolean
  canShowLive: boolean
  isPdf: boolean
  setMode: (mode: SourceReaderMode) => void
}) => {
  const pillLabel =
    isPdf && mode !== "markdown"
      ? "PDF"
      : mode === "markdown"
        ? "Markdown"
        : mode === "stored"
          ? "Saved copy"
          : "Live page"
  const sourceUrl = source.url || undefined
  const sourceLabel = source.name || sourceUrl || "source"
  const pillClass =
    mode === "markdown"
      ? "bg-violet-50 text-violet-700 ring-1 ring-violet-200 dark:bg-violet-950 dark:text-violet-200 dark:ring-violet-800"
      : mode === "stored"
        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:ring-emerald-800"
        : "bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-950 dark:text-sky-200 dark:ring-sky-800"

  return (
    <div className="webkit-app-drag desktop-window-controls-inset shrink-0 border-b border-gray-200 bg-white px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-300">
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`shrink-0 rounded-full px-2 py-0.5 font-medium ${pillClass}`}>
            {pillLabel}
          </span>
          <span className="truncate text-gray-500 dark:text-gray-400">{sourceLabel}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex overflow-hidden rounded border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setMode("markdown")}
              disabled={!canShowMarkdown}
              className={readerModeButtonClass({
                active: mode === "markdown",
                disabled: !canShowMarkdown,
              })}
            >
              Markdown
            </button>
            <button
              type="button"
              onClick={() => setMode("stored")}
              disabled={!hasStoredContent}
              className={`border-l border-gray-200 dark:border-gray-700 ${readerModeButtonClass({
                active: mode === "stored",
                disabled: !hasStoredContent,
              })}`}
            >
              Saved
            </button>
            <button
              type="button"
              onClick={() => setMode("live")}
              disabled={!canShowLive}
              className={`border-l border-gray-200 dark:border-gray-700 ${readerModeButtonClass({
                active: mode === "live",
                disabled: !canShowLive,
              })}`}
            >
              Live
            </button>
          </div>
          {sourceUrl ? (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Open
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const readerModeButtonClass = ({ active, disabled }: { active: boolean; disabled: boolean }) => {
  if (disabled) {
    return [
      "cursor-not-allowed",
      "bg-gray-100",
      "px-2",
      "py-1",
      "text-gray-300",
      "opacity-50",
      "dark:bg-gray-900",
      "dark:text-gray-600",
    ].join(" ")
  }

  if (active) {
    return "bg-gray-100 px-2 py-1 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
  }

  return "px-2 py-1 text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
}

const PdfReader = ({ source }: { source: WebSource }) => {
  const [pdfDocument, setPdfDocument] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [zoom, setZoom] = useState(1)
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })
  const scrollerRef = React.useRef<HTMLDivElement | null>(null)
  const url = sourceContentUrl(source)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setPdfDocument(null)

    const loadingTask = pdfjsLib.getDocument({ url })
    loadingTask.promise
      .then((loadedPdf) => {
        if (cancelled) return
        setPdfDocument(loadedPdf)
        setCurrentPage(1)
        setZoom(1)
        setLoading(false)
      })
      .catch((loadError) => {
        if (cancelled) return
        console.error("Could not load PDF source", loadError)
        setError("Could not load PDF.")
        setLoading(false)
      })

    return () => {
      cancelled = true
      loadingTask.destroy()
    }
  }, [url])

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0, left: 0 })
  }, [currentPage])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || !pdfDocument) return

    const updateViewportSize = (entry?: ResizeObserverEntry) => {
      if (entry) {
        setViewportSize({ width: entry.contentRect.width, height: entry.contentRect.height })
        return
      }

      const style = window.getComputedStyle(scroller)
      setViewportSize({
        width:
          scroller.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
        height:
          scroller.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom),
      })
    }

    updateViewportSize()
    const observer = new ResizeObserver(([entry]) => updateViewportSize(entry))
    observer.observe(scroller)

    return () => observer.disconnect()
  }, [pdfDocument])

  if (loading) {
    return (
      <div className="flex grow items-center justify-center bg-gray-100 text-gray-500 dark:bg-gray-950 dark:text-gray-300">
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5" spin />
      </div>
    )
  }

  if (error || !pdfDocument) {
    return (
      <div className="flex grow items-center justify-center bg-gray-50 p-6 text-sm text-gray-600">
        {error || "Could not load PDF."}
      </div>
    )
  }

  const pageCount = pdfDocument.numPages
  const goToPage = (page: number) => {
    setCurrentPage(Math.min(Math.max(page, 1), pageCount))
  }
  const setClampedZoom = (newZoom: number) => {
    setZoom(Math.min(Math.max(newZoom, 0.5), 2.5))
  }

  return (
    <div className="flex min-h-0 grow flex-col bg-gray-100 dark:bg-gray-950">
      <div className="shrink-0 border-b border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            className="rounded border border-gray-200 px-2 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            Previous
          </button>
          <div className="flex items-center gap-1">
            <span>Page</span>
            <input
              type="number"
              min={1}
              max={pageCount}
              value={currentPage}
              onChange={(event) => goToPage(Number(event.target.value) || 1)}
              className="w-16 rounded border-gray-200 px-2 py-1 text-center text-xs dark:border-gray-700 dark:bg-gray-900"
            />
            <span>of {pageCount}</span>
          </div>
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= pageCount}
            className="rounded border border-gray-200 px-2 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            Next
          </button>
          <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
          <button
            type="button"
            onClick={() => setClampedZoom(zoom - 0.1)}
            disabled={zoom <= 0.5}
            className="rounded border border-gray-200 px-2 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            -
          </button>
          <span className="w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setClampedZoom(zoom + 0.1)}
            disabled={zoom >= 2.5}
            className="rounded border border-gray-200 px-2 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            +
          </button>
        </div>
      </div>
      <div ref={scrollerRef} className="min-h-0 grow overflow-auto px-3 py-5">
        <div className="mx-auto flex min-h-full min-w-0 justify-center">
          <PdfPage
            pdfDocument={pdfDocument}
            pageNumber={currentPage}
            zoom={zoom}
            viewportSize={viewportSize}
          />
        </div>
      </div>
    </div>
  )
}

const PdfPage = ({
  pdfDocument,
  pageNumber,
  zoom,
  viewportSize,
}: {
  pdfDocument: any
  pageNumber: number
  zoom: number
  viewportSize: { width: number; height: number }
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    let cancelled = false
    let renderTask: any = null

    const renderPage = async () => {
      const canvas = canvasRef.current
      if (!canvas || viewportSize.width <= 0 || viewportSize.height <= 0) return

      const page = await pdfDocument.getPage(pageNumber)
      if (cancelled) return

      const baseViewport = page.getViewport({ scale: 1 })
      const fitScale = Math.min(
        viewportSize.width / baseViewport.width,
        viewportSize.height / baseViewport.height,
      )
      const scale = fitScale * zoom
      const viewport = page.getViewport({ scale })
      const outputScale = window.devicePixelRatio || 1
      const context = canvas.getContext("2d")
      if (!context) return

      canvas.width = Math.floor(viewport.width * outputScale)
      canvas.height = Math.floor(viewport.height * outputScale)
      canvas.style.width = `${Math.floor(viewport.width)}px`
      canvas.style.height = `${Math.floor(viewport.height)}px`

      renderTask = page.render({
        canvasContext: context,
        viewport,
        transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
      })
      await renderTask.promise
    }

    renderPage().catch((renderError) => {
      if (!cancelled) console.error("Could not render PDF page", renderError)
    })

    return () => {
      cancelled = true
      renderTask?.cancel()
    }
  }, [pdfDocument, pageNumber, viewportSize.width, viewportSize.height, zoom])

  return (
    <div className="w-full min-w-0">
      <canvas ref={canvasRef} className="mx-auto bg-white shadow-lg ring-1 ring-gray-200" />
    </div>
  )
}

const MobileToolbar = ({
  showLiteratureNotes,
  setShowLiteratureNotes,
  close,
  setReadAndClose,
  source,
}) => {
  return (
    <div className="shrink-0 flex justify-between items-stretch  gap-2 p-2 px-2 border-t border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-950">
      <div className="flex items-stretch gap-2">
        <div
          onClick={() => close()}
          className="flex items-center justify-center px-1 hover:text-sky-400"
        >
          <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
        </div>
        <Button
          onClick={() => setShowLiteratureNotes(!showLiteratureNotes)}
          active={showLiteratureNotes}
        >
          <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4" />
        </Button>
        <NewFleetingNoteModal source={source}>
          {(open) => (
            <Button onClick={() => open()}>
              <FontAwesomeIcon icon={faNoteSticky} className="w-4 h-4" />
            </Button>
          )}
        </NewFleetingNoteModal>
        <NewSourceForm>
          {(open) => (
            <Button onClick={() => open()}>
              <FontAwesomeIcon icon={faLink} className="w-4 h-4" />
            </Button>
          )}
        </NewSourceForm>
      </div>
      <Button onClick={() => setReadAndClose()}>
        <div className="flex items-center gap-2 px-1 text-xs">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4  text-sky-500" />
          Mark read
        </div>
      </Button>
    </div>
  )
}

const NewFleetingNoteModal = ({ source, children }) => {
  const { model, commander } = useNana()
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState("")

  const open = () => setIsOpen(true)
  const submit = () => {
    const id = generatePublicId()
    commander.exec(
      new Commands.CreateFleetingIdeaCommand({
        id,
        fleetingIdea: {
          id,
          content: value,
          sourceId: source.id,
          createdAt: new Date(),
        },
      }),
    )
    setValue("")
    setIsOpen(false)
  }

  return (
    <>
      {children(open)}
      <Modal
        open={isOpen}
        primaryButton={{
          text: "Capture",
          action: () => submit(),
          disabled: false,
        }}
        secondaryButton={{
          text: "Cancel",
          action: () => setIsOpen(false),
          disabled: false,
        }}
        leftButton={{
          text: "",
          action: null,
          disabled: true,
        }}
        close={() => setIsOpen(false)}
      >
        <h1 className="text-lg font-medium border-b border-gray-200 dark:border-gray-600">
          Add quick idea
        </h1>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
          className="mt-4 flex items-start gap-3"
        >
          <FleetingIdeaBox />
          <textarea
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
            }}
            className="w-full p-2 text-sm resize-none bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 outline-none focus:outline-none focus:ring-0 border border-gray-300 dark:border-gray-600"
            placeholder="Quick idea..."
            onKeyDown={(e) => {
              if (e.key == "Escape" || (e.key == "Enter" && e.metaKey)) {
                e.preventDefault()
                //closeAndSave()
              }
            }}
            rows={3}
          />
        </form>
      </Modal>
    </>
  )
}
